import { useCallback, useEffect, useState } from "react";
import { Mail, RefreshCw, FilePlus2, Send, Unplug, Inbox, CalendarPlus, HeartHandshake, Import } from "lucide-react";
import ImportDemosDialog from "./ImportDemosDialog";
import { toast } from "sonner";
import { api } from "./api";

export default function GmailPanel({ onContactsChanged, onOpenContact }) {
  const [status, setStatus] = useState(null);
  const [replies, setReplies] = useState([]);
  const [busy, setBusy] = useState("");
  const [showReplies, setShowReplies] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(() => {
    api.gmailStatus().then(setStatus).catch(() => setStatus({ configured: false, connected: false }));
    api.gmailReplies().then(setReplies).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("provider") === "gmail") {
      toast[p.get("oauth") === "success" ? "success" : "error"](p.get("oauth") === "success" ? "Gmail connected" : "Gmail connection failed");
      window.history.replaceState({}, "", window.location.pathname);
      load();
    }
  }, [load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); ok?.(r); load(); onContactsChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gmail action failed"); }
    finally { setBusy(""); }
  };

  const connect = async () => {
    try {
      const r = await api.gmailConnectUrl();
      window.location.href = r.auth_url || r.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start Gmail connection"); }
  };

  if (!status) return null;

  return (
    <section className="card p-4 space-y-3" data-testid="gmail-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
          <Mail size={16} style={{ color: "var(--cyan)" }} />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="eyebrow">Gmail · drafts, replies, thank-yous &amp; 8:00 Amsterdam digest</div>
          <div className="text-sm font-semibold mt-0.5" data-testid="gmail-status-text">
            {status.connected ? `Connected as ${status.email || "your Gmail"}` : "Not connected"}
            {status.connected && <span className="mono text-xs ml-2" style={{ color: "var(--text-2)" }}>· {status.drafted}/{status.emailable} drafts created</span>}
          </div>
        </div>
        {!status.connected ? (
          <button className="btn btn-amber" onClick={connect} disabled={!status.configured} data-testid="gmail-connect-button" title={status.configured ? "" : "Google OAuth client not configured"}>
            <Mail size={14} /> Connect Gmail
          </button>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <button className="btn btn-sm" disabled={!!busy} data-testid="gmail-create-drafts-button"
              onClick={() => run("drafts", () => api.gmailCreateDrafts(), (r) => {
                if (r.error) { toast.error(`Gmail refused the drafts — ${r.error}`, { duration: 12000 }); return; }
                const reasons = {};
                r.skipped.forEach((x) => { const k = x.reason.replace(/^— \(|\)$/g, ""); reasons[k] = (reasons[k] || 0) + 1; });
                const why = Object.entries(reasons).map(([k, n]) => `${n} ${k}`).join(", ");
                if (r.created.length) toast.success(`${r.created.length} Gmail drafts created${r.skipped.length ? ` · skipped: ${why}` : ""}${r.failed.length ? ` · ${r.failed.length} failed` : ""}`, { duration: 9000 });
                else toast.info(`Nothing new to draft — ${why}. ${reasons["already in Gmail Drafts"] ? "Open Gmail → Drafts to review and send." : ""}`, { duration: 10000 });
              })}>
              <FilePlus2 size={13} /> {busy === "drafts" ? "Drafting…" : "Create Gmail drafts"}
            </button>
            <button className="btn btn-sm btn-ghost" disabled={!!busy} data-testid="gmail-recreate-drafts-button"
              title="Create fresh Gmail drafts for every emailable contact even if one already exists"
              onClick={() => { if (window.confirm("Create a fresh Gmail draft for every emailable contact (existing drafts stay in Gmail)?")) run("drafts", () => api.gmailCreateDrafts(null, true), (r) => toast.success(`${r.created.length} fresh drafts created${r.failed.length ? ` · ${r.failed.length} failed` : ""}`)); }}>
              Recreate
            </button>
            <a className="btn btn-sm btn-ghost" href="https://mail.google.com/mail/#drafts" target="_blank" rel="noreferrer" data-testid="gmail-open-drafts-link">Open Gmail Drafts ↗</a>
            <button className="btn btn-sm" disabled={!!busy} data-testid="gmail-sync-replies-button"
              onClick={() => run("sync", () => api.gmailSyncReplies(), (r) => toast.success(`${r.new_replies.length} new repl${r.new_replies.length === 1 ? "y" : "ies"} · ${r.checked} inboxes checked`))}>
              <RefreshCw size={13} className={busy === "sync" ? "animate-spin" : ""} /> Check replies
            </button>
            <button className="btn btn-sm" disabled={!!busy} data-testid="gmail-send-digest-button"
              onClick={() => run("digest", () => api.gmailSendDigest(), (r) => toast.success(`Digest sent to ${r.to}`))}>
              <Send size={13} /> Email digest now
            </button>
            <button className="btn btn-sm" disabled={!!busy} data-testid="gmail-calendar-sync-button"
              title={status.calendar_scope ? "Create/update Google Calendar events for booked meetings" : "Reconnect Gmail to grant calendar access"}
              onClick={() => run("cal", () => api.gmailCalendarSync(), (r) => {
                toast.success(`${r.synced.length} meeting${r.synced.length === 1 ? "" : "s"} synced to Google Calendar + your website calendar`);
                if (r.unparsed.length) toast.error(`Couldn't read the meeting date for: ${r.unparsed.map((u) => `${u.targetName} ("${u.adeMeetingDate || "empty"}")`).join(", ")} — open the contact and retype it (e.g. "Thu 3pm")`, { duration: 15000 });
              })}>
              <CalendarPlus size={13} /> Sync meetings
            </button>
            <button className="btn btn-sm" disabled={!!busy} data-testid="gmail-import-demos-button" title="Find labels you've sent demos to and add the missing ones"
              onClick={() => setImportOpen(true)}>
              <Import size={13} /> Import demo contacts
            </button>
            <button className="btn btn-sm" disabled={!!busy} data-testid="gmail-followups-button"
              title="Draft the thank-you email for every meeting that already happened (also runs automatically in the 8am digest)"
              onClick={() => run("fu", () => api.gmailDraftFollowUps(), (r) => toast.success(r.drafted.length ? `${r.drafted.length} thank-you draft${r.drafted.length === 1 ? "" : "s"} created in Gmail` : "No past meetings waiting for a thank-you"))}>
              <HeartHandshake size={13} /> {busy === "fu" ? "Drafting…" : "Thank-you drafts"}
            </button>
            <button className="btn btn-sm btn-ghost" disabled={!!busy} data-testid="gmail-disconnect-button"
              onClick={() => run("off", () => api.gmailDisconnect(), () => toast.success("Gmail disconnected"))}>
              <Unplug size={13} />
            </button>
          </div>
        )}
      </div>
      {status.connected && status.last_draft_error && (
        <div className="mono text-[11px] p-2 rounded-lg" style={{ color: "#fca5a5", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)" }} data-testid="gmail-last-error">
          Last Gmail error: {status.last_draft_error}
        </div>
      )}
      {status.connected && (
        <div className="flex flex-wrap items-center gap-4 mono text-[11px]" style={{ color: "var(--text-2)" }}>
          <span>Last reply check: {status.last_sync ? new Date(status.last_sync).toLocaleString() : "never"}</span>
          <span>Last digest: {status.last_digest ? new Date(status.last_digest).toLocaleString() : "never"}</span>
          <span>{status.skipped.length} contacts use a web form / portal — no draft</span>
          <span data-testid="gmail-calendar-status">Calendar: {status.calendar_scope ? `${status.meetings_synced}/${status.meetings_booked} meetings synced · 30-min reminder` : "reconnect Gmail to enable"}</span>
          <button className="tab ml-auto" onClick={() => setShowReplies((v) => !v)} data-testid="gmail-toggle-replies">
            <Inbox size={12} className="inline mr-1" /> Replies ({replies.length})
          </button>
        </div>
      )}
      {status.connected && showReplies && (
        <div className="space-y-2" data-testid="gmail-replies-list">
          {replies.length === 0 && <div className="text-xs" style={{ color: "var(--text-2)" }}>No replies detected yet.</div>}
          {replies.map((r) => (
            <button key={r.id} className="w-full text-left card p-3 hover:border-amber-500/40 transition-colors" style={{ background: "var(--surface-2)" }} onClick={() => onOpenContact?.(r.id)} data-testid={`gmail-reply-${r.id}`}>
              <div className="flex items-center gap-2 text-sm font-semibold">{r.targetName} <span className="eyebrow">{r.company}</span><span className="mono text-[10px] ml-auto" style={{ color: "var(--text-2)" }}>{r.lastReply?.date}</span></div>
              <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>{r.lastReply?.subject}</div>
              <div className="text-xs mt-1">{r.lastReply?.snippet}</div>
              {r.replyDraftId && <div className="mono text-[10px] mt-2" style={{ color: "var(--emerald)" }}>AI reply drafted in Gmail — review &amp; send</div>}
            </button>
          ))}
        </div>
      )}
      <ImportDemosDialog open={importOpen} onClose={() => setImportOpen(false)} onAdded={() => { load(); onContactsChanged?.(); }} />
    </section>
  );
}
